import { useEffect, useMemo, useState } from "react";
import { resolveStorageUrls, type ParseOptions } from "@/utils/storageRefs";

/**
 * Openable links for stored file values (see utils/storageRefs.ts). Resolves
 * in one batch, re-resolves when the values change. `urls[value]` is
 * undefined while loading, then a URL or null.
 */
export function useSignedUrls(
  values: Array<string | null | undefined>,
  opts: ParseOptions = {}
): { urls: Record<string, string | null>; loading: boolean } {
  const key = useMemo(
    () => JSON.stringify([values.filter(Boolean), opts.defaultBucket ?? null, opts.defaultProject ?? null]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values.join("\u0000"), opts.defaultBucket, opts.defaultProject]
  );
  const [urls, setUrls] = useState<Record<string, string | null>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const [list, defaultBucket, defaultProject] = JSON.parse(key) as [
      string[],
      string | null,
      ParseOptions["defaultProject"] | null,
    ];
    if (list.length === 0) {
      setUrls({});
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    resolveStorageUrls(list, {
      defaultBucket: defaultBucket ?? undefined,
      defaultProject: defaultProject ?? undefined,
    }).then((resolved) => {
      if (cancelled) return;
      setUrls(resolved);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [key]);

  return { urls, loading };
}

/** One stored value → its openable link (undefined while loading). */
export function useSignedUrl(
  value: string | null | undefined,
  opts: ParseOptions = {}
): { url: string | null | undefined; loading: boolean } {
  const { urls, loading } = useSignedUrls([value], opts);
  return { url: value ? urls[value] : null, loading };
}
